import { Component, computed, inject, Signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { SchedulerValidationUtils } from '@lb/components';
import { SchedulerFormService } from '@lb/schedule/services/scheduler-form.service';
import { filter, startWith, switchMap } from 'rxjs';
import { Day, IMDDropDownOption, MDContentBoxComponent, MDDatePickerInputDirective, MDDropDownComponent, MDIconComponent } from '@melodic-ui/components';
import { SchedulerStateService } from '@lb/schedule';
import { format, isPast, isToday } from 'date-fns';
import { TimezoneDropdownComponent } from '@lb/components/timezone-dropdown/timezone-dropdown.component';

@Component({
	selector: 'lb-schedule-configuration',
	standalone: true,
	imports: [MDIconComponent, MDDatePickerInputDirective, MDContentBoxComponent, MDDropDownComponent, TimezoneDropdownComponent],
	templateUrl: './schedule-configuration.component.html',
	styleUrl: './schedule-configuration.component.scss'
})
export class ScheduleConfigurationComponent {
	private readonly _schedulerFormService = inject(SchedulerFormService);
	private readonly _scheduleStateService = inject(SchedulerStateService);

	private readonly _frequencyFormSignal = toSignal(
		toObservable(this._schedulerFormService.step1Form).pipe(
			filter((form) => !!form),
			switchMap((form) => form.get('frequency')!.valueChanges.pipe(startWith(form.get('frequency')!.value)))
		),
		{ initialValue: undefined }
	);

	private readonly _startDateFormSignal = toSignal(
		toObservable(this._schedulerFormService.step1Form).pipe(
			filter((form) => !!form),
			switchMap((form) => form.get('startDate')!.valueChanges.pipe(startWith(form.get('startDate')!.value)))
		),
		{ initialValue: undefined }
	);

	private readonly _timeZoneFormSignal = toSignal(
		toObservable(this._schedulerFormService.step1Form).pipe(
			filter((form) => !!form),
			switchMap((form) => form.get('timeZone')!.valueChanges.pipe(startWith(form.get('timeZone')!.value)))
		),
		{ initialValue: undefined }
	);

	private readonly _enrollmentStatusFormSignal = toSignal(
		toObservable(this._schedulerFormService.step1Form).pipe(
			filter((form) => !!form),
			switchMap((form) => form.get('enrollmentStatus')!.valueChanges.pipe(startWith(form.get('enrollmentStatus')!.value)))
		),
		{ initialValue: undefined }
	);

	isAdmin = this._scheduleStateService.isAdmin;
	canChangePreferredTimes = this._scheduleStateService.canChangePreferredTimes;
	canChangeTimezone = this._scheduleStateService.canChangeTimezone;

	private readonly _allFrequencies = this._scheduleStateService.programFrequencies;
	private readonly _enrollment = this._scheduleStateService.enrollment;

	lessonFrequencyOptions = computed<Array<IMDDropDownOption>>(() => {
		const enrollment = this._enrollment();
		const allFrequencies = this._allFrequencies();
		const isAdmin = this.isAdmin();

		if (!enrollment || !allFrequencies.length) {
			// Fallback to default if no enrollment or frequencies loaded yet
			return [
				{ label: '1 lesson/week', value: 1, selected: false },
				{ label: '2 lessons/week', value: 2, selected: false },
				{ label: '3 lessons/week', value: 3, selected: false }
			];
		}

		// Filter frequencies for current program
		const programFrequencies = allFrequencies
			.filter((f) => f.programID === enrollment.program?.programID)
			.filter((f) => !f.isAdminOnly || isAdmin)
			.sort((a, b) => a.frequency - b.frequency);

		// If no frequencies found for program (shouldn't happen with migration), return default
		if (programFrequencies.length === 0) {
			return [
				{ label: '1 lesson/week', value: 1, selected: false },
				{ label: '2 lessons/week', value: 2, selected: false },
				{ label: '3 lessons/week', value: 3, selected: false }
			];
		}

		// Map to dropdown options
		return programFrequencies.map((f) => ({
			label: f.label || (f.frequency === 1 ? '1 lesson/week' : `${f.frequency} lessons/week`),
			value: f.frequency,
			selected: false
		}));
	});

	selectedFrequency: Signal<number | undefined> = this._frequencyFormSignal;
	selectedTimeZone: Signal<number | undefined> = this._timeZoneFormSignal;
	selectedEnrollmentStatus: Signal<number | undefined> = this._enrollmentStatusFormSignal;

	selectedStartDate: Signal<string | undefined> = computed(() => {
		// Use the reactive form signal first, then fall back to enrollment history
		const formValue = this._startDateFormSignal();

		console.log(this._startDateFormSignal());
		if (formValue) {
			return formValue;
		}

		// Final fallback to the minimum allowed start date (today + 7 days), so the default
		// matches the non-admin start-date requirement enforced by the validator.
		const defaultDate = format(SchedulerValidationUtils.getMinimumStartDate(), 'yyyy-MM-dd');
		return defaultDate;
	});

	// Local update methods - no backend calls, just update form values
	updateLessonFrequency = (newFrequency: unknown) => {
		const form = this._schedulerFormService.step1Form();
		if (form) {
			form.get('frequency')?.setValue(newFrequency as number);
		}
	};

	updateScheduleStartDate = (selectedDate: string) => {
		const form = this._schedulerFormService.step1Form();
		if (form) {
			form.get('startDate')?.setValue(selectedDate);
		}
	};

	updateTimeZone = (selectedTimeZone: unknown) => {
		const form = this._schedulerFormService.step1Form();
		if (form) {
			form.get('timeZone')?.setValue(selectedTimeZone as number);
		}
	};

	updateEnrollmentStatus = (selectedStatus: unknown) => {
		const form = this._schedulerFormService.step1Form();
		if (form) {
			form.get('enrollmentStatus')?.setValue(selectedStatus as number);
		}
	};

	disableDay(): (day: Day) => boolean {
		if (this.isAdmin()) {
			return (day: Day) => !isToday(day.date) && isPast(day.date);
		}

		return (day: Day) => SchedulerValidationUtils.isDateTooEarly(day.date);
	}
}
